import type { GamePreset, OcrModelResource } from '@/presets/types'

export type OcrModelKey = 'detection' | 'recognition'

export interface OcrCacheProgress {
  readonly model: OcrModelKey
  readonly progress: number
  readonly fromCache: boolean
}

export interface OcrCachePreparationResult {
  readonly cacheDegraded: boolean
  readonly usedTemporaryResources: boolean
  releaseTemporaryResources(): void
}

export interface OcrResourceCacheOptions {
  readonly connectionTimeoutMs?: number
  readonly readStallTimeoutMs?: number
  readonly cacheOperationTimeoutMs?: number
}

export const OCR_MODEL_CACHE_NAME = 'maple-log-ocr-models-v3'
const MODEL_VERSION_PARAM = '__maple_log_model_version'
const DEFAULT_CONNECTION_TIMEOUT_MS = 15_000
const DEFAULT_READ_STALL_TIMEOUT_MS = 20_000
const DEFAULT_CACHE_OPERATION_TIMEOUT_MS = 10_000

interface TemporaryModelResource {
  readonly bytes: Uint8Array<ArrayBuffer>
  readonly contentType: string
  readonly sourceUrl: string
  readonly token: symbol
}

interface DownloadedModelResource {
  readonly bytes: Uint8Array<ArrayBuffer>
  readonly contentType: string
  readonly sourceUrl: string
}

interface OperationSuccess<T> {
  readonly ok: true
  readonly value: T
}

interface OperationFailure {
  readonly ok: false
  readonly error: unknown
  readonly timedOut: boolean
}

type OperationResult<T> = OperationSuccess<T> | OperationFailure

const temporaryModelResources = new Map<string, TemporaryModelResource>()

export class OcrResourceLoadError extends Error {
  readonly code = 'OCR_RESOURCE_UNAVAILABLE'

  constructor(message: string) {
    super(message)
    this.name = 'OcrResourceLoadError'
  }
}

export function isOcrResourceLoadError(error: unknown): error is OcrResourceLoadError {
  return error instanceof OcrResourceLoadError
    || (typeof error === 'object' && error !== null && 'code' in error && error.code === 'OCR_RESOURCE_UNAVAILABLE')
}

export interface ResolvedOcrModels {
  readonly models: GamePreset['remoteResources']['ocrModels']
  release(): void
}

export async function ensureOcrModelsCached(
  models: GamePreset['remoteResources']['ocrModels'],
  onProgress: (progress: OcrCacheProgress) => void,
  options: OcrResourceCacheOptions = {},
): Promise<OcrCachePreparationResult> {
  const resources = modelResources(models)
  const cacheTimeoutMs = options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS
  const temporaryLeases: { readonly key: string; readonly token: symbol }[] = []
  let cacheDegraded = false
  let cache = await openModelCache(cacheTimeoutMs)
  if (!cache) {
    cacheDegraded = true
  }

  try {
    for (let index = 0; index < resources.length; index += 1) {
      const [model, resource] = resources[index]!
      const cacheRequest = createModelCacheRequest(resource)
      let cached: Response | undefined

      if (cache) {
        const match = await settleOperation(cache.match(cacheRequest), cacheTimeoutMs, 'cache read')
        if (match.ok) {
          cached = match.value
        } else {
          cache = null
          cacheDegraded = true
        }
      }

      if (cached) {
        onProgress({ model, progress: (index + 1) / resources.length, fromCache: true })
        continue
      }

      const downloaded = await downloadModelResource(resource, (resourceProgress) => {
        onProgress({
          model,
          progress: (index + resourceProgress) / resources.length,
          fromCache: false,
        })
      }, options)
      const lease = retainTemporaryModel(resource, downloaded)
      temporaryLeases.push(lease)

      if (cache) {
        const cacheWrite = await settleOperation(
          cache.put(cacheRequest, responseFromDownload(downloaded)),
          cacheTimeoutMs,
          'cache write',
        )
        if (!cacheWrite.ok) {
          cache = null
          cacheDegraded = true
        } else {
          releaseTemporaryLeases([lease])
          temporaryLeases.pop()
        }
      }
    }

    if (cache) {
      const cleanup = await settleOperation(
        removeOutdatedModels(cache, resources.map(([, resource]) => modelCacheKey(resource))),
        cacheTimeoutMs,
        'cache cleanup',
      )
      if (!cleanup.ok) cacheDegraded = true
    }
  } catch (error) {
    releaseTemporaryLeases(temporaryLeases)
    throw error
  }

  let released = false
  return {
    cacheDegraded,
    usedTemporaryResources: temporaryLeases.length > 0,
    releaseTemporaryResources() {
      if (released) return
      released = true
      releaseTemporaryLeases(temporaryLeases)
    },
  }
}

export async function resolveCachedOcrModels(
  models: GamePreset['remoteResources']['ocrModels'],
  options: OcrResourceCacheOptions = {},
): Promise<ResolvedOcrModels> {
  const cacheTimeoutMs = options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS
  let cache = await openModelCache(cacheTimeoutMs)
  const objectUrls: string[] = []
  const resolved: Partial<Record<OcrModelKey, OcrModelResource>> = {}

  for (const [key, resource] of modelResources(models)) {
    const cacheKey = modelCacheKey(resource)
    let blob: Blob | null = null

    if (cache) {
      const match = await settleOperation(cache.match(createModelCacheRequest(resource)), cacheTimeoutMs, 'cache read')
      if (match.ok && match.value) {
        const body = await settleOperation(match.value.blob(), cacheTimeoutMs, 'cache response read')
        if (body.ok) blob = body.value
        else cache = null
      } else if (!match.ok) {
        cache = null
      }
    }

    const temporary = temporaryModelResources.get(cacheKey)
    if (!blob && temporary) blob = new Blob([temporary.bytes], { type: temporary.contentType })

    if (blob && typeof URL.createObjectURL === 'function') {
      const objectUrl = URL.createObjectURL(blob)
      objectUrls.push(objectUrl)
      resolved[key] = { ...resource, url: objectUrl }
    } else if (temporary) {
      resolved[key] = { ...resource, url: temporary.sourceUrl }
    } else {
      resolved[key] = resource
    }
  }

  let released = false
  return {
    models: {
      detection: resolved.detection!,
      recognition: resolved.recognition!,
    },
    release() {
      if (released) return
      released = true
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    },
  }
}

export function modelCacheKey(resource: OcrModelResource): string {
  const url = new URL(resource.url, window.location.href)
  url.searchParams.set(MODEL_VERSION_PARAM, resource.version)
  return url.href
}

function modelResources(
  models: GamePreset['remoteResources']['ocrModels'],
): readonly (readonly [OcrModelKey, OcrModelResource])[] {
  return [
    ['detection', models.detection],
    ['recognition', models.recognition],
  ]
}

function createModelCacheRequest(resource: OcrModelResource): Request {
  return new Request(modelCacheKey(resource), { credentials: 'omit' })
}

async function openModelCache(timeoutMs: number): Promise<Cache | null> {
  if (typeof globalThis.caches === 'undefined') return null
  const opened = await settleOperation(caches.open(OCR_MODEL_CACHE_NAME), timeoutMs, 'cache open')
  return opened.ok ? opened.value : null
}

async function downloadModelResource(
  resource: OcrModelResource,
  onProgress: (progress: number) => void,
  options: OcrResourceCacheOptions,
): Promise<DownloadedModelResource> {
  const urls = [resource.url, ...(resource.fallbackUrls ?? [])]
  const failures: string[] = []
  let reportedProgress = 0

  for (const url of urls) {
    const controller = new AbortController()
    try {
      const fetched = await settleOperation(
        fetch(new Request(url, {
          cache: 'no-store',
          credentials: 'omit',
          mode: 'cors',
          signal: controller.signal,
        })),
        options.connectionTimeoutMs ?? DEFAULT_CONNECTION_TIMEOUT_MS,
        'connection',
        () => controller.abort(),
      )
      if (!fetched.ok) {
        failures.push(`${url}: ${errorMessage(fetched.error)}`)
        continue
      }
      if (!fetched.value.ok) {
        failures.push(`${url}: HTTP ${fetched.value.status}`)
        continue
      }

      const body = await consumeResponse(
        fetched.value,
        (progress) => {
          reportedProgress = Math.max(reportedProgress, progress)
          onProgress(reportedProgress)
        },
        options.readStallTimeoutMs ?? DEFAULT_READ_STALL_TIMEOUT_MS,
        controller,
      )
      return { ...body, sourceUrl: url }
    } catch (error) {
      failures.push(`${url}: ${errorMessage(error)}`)
    } finally {
      controller.abort()
    }
  }

  throw new OcrResourceLoadError(`OCR resource request failed (${failures.join('; ')})`)
}

async function consumeResponse(
  response: Response,
  onProgress: (progress: number) => void,
  stallTimeoutMs: number,
  controller: AbortController,
): Promise<Pick<DownloadedModelResource, 'bytes' | 'contentType'>> {
  const total = Number(response.headers.get('content-length')) || 0
  const contentType = response.headers.get('content-type') ?? 'application/octet-stream'
  if (!response.body) {
    const body = await settleOperation(response.arrayBuffer(), stallTimeoutMs, 'response read', () => controller.abort())
    if (!body.ok) throw body.error
    onProgress(1)
    return { bytes: new Uint8Array(body.value), contentType }
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  let loaded = 0
  while (true) {
    const read = await settleOperation(reader.read(), stallTimeoutMs, 'response read', () => {
      controller.abort()
      void reader.cancel().catch(() => {})
    })
    if (!read.ok) throw read.error
    if (read.value.done) break
    const chunk = new Uint8Array(read.value.value.byteLength)
    chunk.set(read.value.value)
    chunks.push(chunk)
    loaded += chunk.byteLength
    if (total > 0) onProgress(Math.min(1, loaded / total))
  }
  onProgress(1)
  const bytes = new Uint8Array(loaded)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.byteLength
  }
  return { bytes, contentType }
}

function responseFromDownload(downloaded: DownloadedModelResource): Response {
  return new Response(downloaded.bytes, {
    status: 200,
    headers: {
      'content-length': String(downloaded.bytes.byteLength),
      'content-type': downloaded.contentType,
      'x-maple-log-source': downloaded.sourceUrl,
    },
  })
}

function retainTemporaryModel(
  resource: OcrModelResource,
  downloaded: DownloadedModelResource,
): { readonly key: string; readonly token: symbol } {
  const key = modelCacheKey(resource)
  const token = Symbol(key)
  temporaryModelResources.set(key, { ...downloaded, token })
  return { key, token }
}

function releaseTemporaryLeases(leases: readonly { readonly key: string; readonly token: symbol }[]): void {
  for (const lease of leases) {
    if (temporaryModelResources.get(lease.key)?.token === lease.token) {
      temporaryModelResources.delete(lease.key)
    }
  }
}

async function removeOutdatedModels(cache: Cache, currentUrls: readonly string[]): Promise<void> {
  const current = new Set(currentUrls.map((url) => new URL(url, window.location.href).href))
  const requests = await cache.keys()
  await Promise.all(requests
    .filter((request) => !current.has(request.url))
    .map((request) => cache.delete(request)))
}

function settleOperation<T>(
  operation: Promise<T>,
  timeoutMs: number,
  label: string,
  onTimeout?: () => void,
): Promise<OperationResult<T>> {
  return new Promise((resolve) => {
    let settled = false
    const timer = globalThis.setTimeout(() => {
      if (settled) return
      settled = true
      onTimeout?.()
      resolve({ ok: false, error: new Error(`${label} timed out`), timedOut: true })
    }, Math.max(0, timeoutMs))

    operation.then(
      (value) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timer)
        resolve({ ok: true, value })
      },
      (error: unknown) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timer)
        resolve({ ok: false, error, timedOut: false })
      },
    )
  })
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'network error'
}
